import { useCallback, useMemo } from "react";
import type { UseFormReturn } from "react-hook-form";
import { useWatch } from "react-hook-form";

import type { StaffTaskV2FormValues } from "../staff-task.utils";

export const PERSON_REQUIRED_MESSAGE = "Assign to a person is required";

type Form = Pick<
  UseFormReturn<StaffTaskV2FormValues>,
  "control" | "getValues" | "setValue" | "trigger"
>;

type TeamOption = { label: string; value: string } | null;

/**
 * Teams (TEAMS_FEATURE): the "Assign to a person" / "Send to a team" pair on the New Task and Edit Task forms.
 *
 * A task needs a person OR a team. The person rule reads the team when it RUNS (`getValues`), rather than
 * a `required` flag that changes on the next render: picking a team blanks the person and re-checks it in
 * the same tick, before that render, so a toggled flag still said "required" and left a red
 * "Assign to a person is required" under a valid form (Omar, 2026-11-03). Every team change re-checks the
 * person field through `trigger`, which also recomputes `isValid` (Save is gated on it).
 *
 * Flag off, nothing here applies: the field keeps its plain `required` rule, exactly as before Teams.
 */
export default function useTeamAssigneeField({
  form,
  enableTeams
}: {
  form: Form;
  enableTeams: boolean;
}) {
  const { control, getValues, setValue, trigger } = form;
  const team = useWatch({ control, name: "team" });
  const assignee = useWatch({ control, name: "assignee" });

  const rules = useMemo(
    () =>
      enableTeams
        ? {
            // Overrides the field's own `required` rule (spread after it); the asterisk still follows
            // `required` below, which is display only.
            required: false,
            validate: (value: unknown) =>
              Boolean((value as TeamOption)?.value) ||
              Boolean(getValues("team")?.value) ||
              PERSON_REQUIRED_MESSAGE
          }
        : undefined,
    [enableTeams, getValues]
  );

  /**
   * Call from the team field's `onOptionSelected` (the team value is already set by then).
   * `clearPerson`: picking a team leaves the task with nobody until a member takes it, so the person the
   * form pre-filled (the creator) is cleared. The edit form passes false when the team did not change.
   */
  const onTeamSelected = useCallback(
    (picked: TeamOption, { clearPerson = true } = {}) => {
      if (picked && clearPerson) {
        setValue("assignee", null, { shouldDirty: true });
      }
      // Re-check now that the team is in place: clears a stale error, or brings "required" back when
      // the team was removed and nobody is picked. Fire and forget; it only updates form state.
      void trigger("assignee");
    },
    [setValue, trigger]
  );

  const teamName = team?.value ? team.label : null;
  const personName = assignee?.value ? assignee.label : null;
  const hint =
    !enableTeams || !teamName
      ? null
      : personName
        ? // True whether the person was picked here or took it from the team earlier (Edit Task).
          `${personName} is on it. ${teamName} can still follow it, but nobody needs to take it.`
        : `Left empty on purpose: the first member of ${teamName} to click Take it owns it. Pick a person only to give it to someone directly.`;

  return {
    rules,
    /** Display only (the asterisk): a person is needed only when no team is picked. */
    required: !(enableTeams && team?.value),
    onTeamSelected,
    hint
  };
}
